import { Video, Youtube, ExternalLink, RefreshCw, Archive } from "lucide-react";
import { Button } from "../../ui/button";
import GameClipsManager from "../../GameClips/GameClipsManager";

export const VODPanel = ({ 
  broadcast,
  vodForm,
  setVodForm,
  onSaveVod,
  onSyncScores,
  onArchive,
  savingVod,
  syncingScores,
  archiving,
  onRefreshBroadcast
}) => {
  const hasRecording = broadcast.recording_url || broadcast.youtube_vod_url;
  const isCompleted = broadcast.is_completed;
  
  // Allow clips for completed games OR past games (scheduled date is in the past)
  const isPastGame = () => {
    if (!broadcast.scheduled_date) return false;
    const gameDate = new Date(broadcast.scheduled_date + 'T23:59:59');
    return gameDate < new Date();
  };
  const canAddClips = isCompleted || isPastGame();

  return (
    <div className="border-t border-purple-500/30 p-4 bg-purple-500/5">
      <div className="flex items-center gap-2 mb-4">
        <Video className="w-5 h-5 text-purple-400" />
        <h5 className="font-secondary text-sm font-bold uppercase text-purple-400">On-Demand Recording</h5>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* YouTube VOD URL */}
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase mb-1 flex items-center gap-1">
            <Youtube className="w-3 h-3 text-red-500" />
            YouTube VOD URL
          </label>
          <input 
            type="text"
            value={vodForm.youtube_vod_url}
            onChange={(e) => setVodForm({ ...vodForm, youtube_vod_url: e.target.value })}
            placeholder="https://www.youtube.com/watch?v=..."
            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="vod-youtube-input"
          />
          <p className="text-[10px] text-zinc-600 mt-1">Paste the YouTube video URL for the recorded game</p>
        </div>
        
        {/* Alternative Recording URL */}
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase mb-1 flex items-center gap-1">
            <ExternalLink className="w-3 h-3" />
            Alternative Recording URL
          </label>
          <input 
            type="text"
            value={vodForm.recording_url}
            onChange={(e) => setVodForm({ ...vodForm, recording_url: e.target.value })}
            placeholder="https://..."
            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="vod-recording-input"
          />
          <p className="text-[10px] text-zinc-600 mt-1">For non-YouTube recordings (MP4, HLS, etc.)</p>
        </div>
      </div>
      
      {/* Final Score Entry */}
      <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-zinc-900/50 border border-white/5">
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase mb-1">
            {broadcast.home_team?.name || "Home"} Score
          </label>
          <input 
            type="number"
            value={vodForm.home_score}
            onChange={(e) => setVodForm({ ...vodForm, home_score: e.target.value })}
            placeholder="0"
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-center text-lg font-bold"
            data-testid="vod-home-score"
          />
        </div>
        <div>
          <label className="block text-[10px] text-zinc-500 uppercase mb-1">
            {broadcast.away_team?.name || "Away"} Score
          </label>
          <input 
            type="number"
            value={vodForm.away_score}
            onChange={(e) => setVodForm({ ...vodForm, away_score: e.target.value })}
            placeholder="0"
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-center text-lg font-bold"
            data-testid="vod-away-score"
          />
        </div>
      </div>
      
      {/* Sync from Stats Link Button */}
      {broadcast.stats_link && (
        <div className="mb-4">
          <Button
            type="button"
            onClick={() => onSyncScores(broadcast.id)}
            disabled={syncingScores}
            className="w-full bg-green-600 text-white hover:bg-green-700 text-xs px-4 py-2 h-auto rounded-none flex items-center justify-center gap-2"
            data-testid="sync-scores-btn"
          >
            <RefreshCw className={`w-4 h-4 ${syncingScores ? 'animate-spin' : ''}`} />
            {syncingScores ? 'Syncing...' : 'Sync Final Score from Stats'}
          </Button>
          <p className="text-[10px] text-zinc-600 text-center mt-1">
            Auto-fetch final score from {broadcast.stats_provider || 'stats link'}
          </p>
        </div>
      )}
      
      <div className="flex gap-3">
        <Button
          type="button"
          onClick={() => onSaveVod(broadcast.id)}
          disabled={savingVod}
          className="flex-1 bg-purple-600 text-white hover:bg-purple-700 text-xs px-4 py-2 h-auto rounded-none"
          data-testid="save-vod-btn"
        >
          {savingVod ? 'Saving...' : 'Save VOD Info'}
        </Button>
        
        {hasRecording && (
          <Button
            type="button"
            onClick={() => onArchive(broadcast.id)}
            disabled={archiving}
            className="bg-orange-600 text-white hover:bg-orange-700 text-xs px-4 py-2 h-auto rounded-none flex items-center gap-2"
            data-testid="archive-btn"
          >
            <Archive className="w-4 h-4" />
            {archiving ? 'Archiving...' : 'Archive to On-Demand'}
          </Button>
        )}
      </div>
      
      {/* Game Clips Section - For completed games OR past games */}
      {canAddClips && (
        <GameClipsManager
          broadcastId={broadcast.id}
          clips={broadcast.game_clips || []}
          homeTeam={broadcast.home_team}
          awayTeam={broadcast.away_team}
          onClipsUpdated={onRefreshBroadcast}
        />
      )}
    </div>
  );
};
